import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Plus } from 'lucide-react';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { holdTypesService } from '../../../services/hold-types';
import type { HoldType } from '../../../types/hold-type';
import { HoldTypeSwatch } from './HoldTypeSwatch';

/** As many as fit the dialog's scroller without a pager of its own. */
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

interface HoldTypeDialogProps {
  /** The type's name — what the climb form posts back as `hold_color`. */
  value: string;
  onChange: (name: string) => void;
  locationId: string | number;
  /** Mark it as an answer the form is still waiting for — see the save button. */
  invalid?: boolean;
  /**
   * How the thing you press looks.
   *
   * `inline` is the climb editor's footer row: the label and the answer on one
   * line, no chrome. `field` is a form control with a label of its own above it,
   * so it is drawn like the selects it sits among — the scan editors.
   */
  as?: 'inline' | 'field';
}

/**
 * What a climb's holds look like, picked from the place's own list.
 *
 * A gym sets its walls in a handful of hold types and every climb is one of
 * them, so the list comes first and typing only narrows it. What is chosen
 * travels as the type's *name*: the server resolves it against the list, which
 * is what keeps this the same one-field answer every form has always sent.
 *
 * **Adding one adds it for real**, on the spot, to the gym's list. That is what
 * makes it usable where a whole wall is being named at once — the scan editor,
 * where the colour you invent for the first problem has to be there for the
 * second — and it is what the button says it does. The cost is a type nobody
 * ends up using, which shows in the route-setting panel as "0 blocchi" with a
 * bin beside it.
 *
 * Offered only to the people who set at this place (`canManage`, which is the
 * server's answer rather than a guess). A member putting up a problem of their
 * own picks from what the gym provides — and the save is refused if they get
 * past the missing button somehow.
 */
export function HoldTypeDialog({
  value,
  onChange,
  locationId,
  invalid = false,
  as = 'inline',
}: HoldTypeDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [types, setTypes] = useState<HoldType[]>([]);
  const [canManage, setCanManage] = useState(false);
  // Held apart from the list, which is only ever the current search's page: a
  // filter that narrows the chosen type away must not take its colours off the
  // button.
  const [chosen, setChosen] = useState<HoldType | null>(null);
  const [adding, setAdding] = useState(false);

  const typed = filter.trim();

  // Asked of the server rather than filtered here: the list is paged, so what
  // is in hand is not the whole vocabulary and a local filter would quietly
  // hide the rest of it. The first read happens on mount rather than on open,
  // because the button under the dialog shows the chosen type's colours.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      holdTypesService
        .list(locationId, { query: typed, perPage: PAGE_SIZE })
        .then(({ types: found, canManage: allowed }) => {
          if (cancelled) return;
          setTypes(found);
          setCanManage(allowed);
        })
        .catch(() => {
          if (!cancelled) setTypes([]);
        });
    }, typed ? SEARCH_DEBOUNCE_MS : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [locationId, typed]);

  // The colours of whatever `value` names, once a page containing it comes back
  // — which covers a climb being corrected, where the answer arrives from the
  // form rather than from a tap on the list.
  useEffect(() => {
    const match = types.find((type) => type.name.toLowerCase() === value.trim().toLowerCase());
    if (match) setChosen(match);
  }, [types, value]);

  // Only when it is genuinely new: offering "red" as a new type while "red" sits
  // in the list above is two rows that do the same thing.
  const isNew =
    canManage && typed.length > 0 && !types.some((type) => type.name.toLowerCase() === typed.toLowerCase());

  const choose = (type: HoldType | null, name: string) => {
    setChosen(type);
    onChange(name);
    setOpen(false);
    setFilter('');
  };

  // Adding one puts it on the gym's list there and then, so the next problem on
  // this wall is offered it. A name the gym already had comes back from the
  // server as a refusal; falling back to the wording keeps the answer, and the
  // list will show the row it clashed with next time it is opened.
  const addTyped = async () => {
    if (adding) return;
    setAdding(true);
    try {
      choose(await holdTypesService.create(locationId, { name: typed }), typed);
    } catch {
      choose(null, typed);
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      {as === 'field' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="climb-hold-color"
          aria-invalid={invalid || undefined}
          aria-label={t('climbCreate.holdColor')}
          className={`flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
            invalid ? 'border-destructive' : 'border-input'
          }`}
        >
          <span className="flex min-w-0 items-center gap-2">
            {chosen && chosen.name === value && <HoldTypeSwatch type={chosen} />}
            <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
              {value || t('climbEditor.holdColor.none')}
            </span>
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="climb-hold-color"
          aria-invalid={invalid || undefined}
          className="flex items-center gap-2 text-sm"
        >
          <span className={invalid ? 'text-red-500' : 'text-muted-foreground'}>
            {t('climbCreate.holdColor')}
          </span>
          <span className="flex items-center gap-1.5 font-medium">
            {chosen && chosen.name === value && <HoldTypeSwatch type={chosen} />}
            {value || (
              <span className={invalid ? 'text-red-500' : 'text-muted-foreground'}>
                {t('climbEditor.holdColor.none')}
              </span>
            )}
          </span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('climbCreate.holdColor')}</DialogTitle>
            <DialogDescription>
              {canManage ? t('climbEditor.holdColor.intro') : t('climbEditor.holdColor.introMember')}
            </DialogDescription>
          </DialogHeader>

          <Input
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={
              canManage
                ? t('climbEditor.holdColor.filter')
                : t('climbEditor.holdColor.filterMember')
            }
            aria-label={t('climbEditor.holdColor.filter')}
            autoFocus
          />

          <div className="max-h-64 space-y-1 overflow-y-auto">
            {types.map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => choose(type, type.name)}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                  type.name === value ? 'bg-muted font-medium' : ''
                }`}
              >
                <HoldTypeSwatch type={type} />
                {type.name}
              </button>
            ))}

            {isNew && (
              <button
                type="button"
                onClick={addTyped}
                disabled={adding}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-muted disabled:opacity-60"
              >
                {adding ? (
                  <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
                ) : (
                  <Plus className="size-4 shrink-0" aria-hidden />
                )}
                {t('climbEditor.holdColor.addNew', { color: typed })}
              </button>
            )}

            {types.length === 0 && !isNew && (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {typed && !canManage
                  ? t('climbEditor.holdColor.noMatchMember')
                  : t('climbEditor.holdColor.empty')}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
